/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { Port, columns as portColumns, shapePortData } from "./PortData";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";

// Example request functions (replace with your real API calls)
import {
  getHardwareListByDataCenter,
  getPortsById,
  addPort,
  updatePort,
  deletePort,
  getListPortsByHardwareId,
  getAllPorts,
  getKEPorts,
  getKEPortById,
  getTenants,
} from "./request/request";

import { getDataCentersIdsNames } from "../../Hardware/request/request";
import { useAppSelector } from "@redux/hooks";
import { buildPredicate, FilterField, labelId, ParsedFilter as OldParsed, parseToken } from "../../../../helpers/advancedFilters";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { PortViewModal } from "./Modals/PortViewModal";
import { PortCreateModal } from "./Modals/PortCreateModal";
import { PortEditModal } from "./Modals/PortEditModal";
import { usePortForm } from "./hooks/usePortForm";


// ---------- Interfaces (Adjust to match your actual API shapes) -----------

// Datacenter
interface DataCenterItem {
  datacenterId: string; // or number
  name: string;
  displayName?: string;
}

interface HardwareDropdownItem {
  id: number;
  hostname: string;
}

interface PortDropdownItem {
  id: number;
  portNumber: string;
  portDescription: string;
  portStatus: string;
}

interface TenantItem {
  id: number;
  Name: string;
  nameDisplay: string;
  chargebeeCustomerId: string | null;
  tenantTypeId: number;
  TenantType: {
    id: number;
    name: string;
  };
}

// Hardware
interface HardwareItem {
  id: number;
  datacenterId: string;
  serialNumber: string;
  model: string;
  vendor: string;
  hostname: string;
  status: string;
}

// Por

// This shape is returned by getPortsByHardwareId
interface PortsByHardwareResponse {
  hardware: HardwareItem;
  ports: Port[];
}

// ---------- Main Component -----------------------------------------------

export default function PortRMPage() {
  // Auth
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // 1) Datacenter dropdown states
  const [dataCenterOptions, setDataCenterOptions] = useState<string[]>([]);
  const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
  const [, setSelectedDataCenterId] = useState<string | null>("");
  const [selectedDataCenterName, setSelectedDataCenterName] =
    useState<string>("");

  // 2) Hardware dropdown states
  const [hardwareList, setHardwareList] = useState<HardwareDropdownItem[]>([]);
  const [hardwareOptions, setHardwareOptions] = useState<string[]>([]);
  const [selectedHardwareId, setSelectedHardwareId] = useState<number | null>(
    null
  );
  // For the modal's DC + hardware selection
  const [, setModalDcId] = useState<string | null>(null);
  const [modalDcName, setModalDcName] = useState<string>("");
  const [modalHwId, setModalHwId] = useState<number | null>(null);
  const [, setModalHwName] = useState<string>("");
  // For the modal's port selection
  const [modalPortsList, setModalPortsList] = useState<any[]>([]);
  const [modalPortOptions, setModalPortOptions] = useState<string[]>([]);
  const [selectedPortId, setSelectedPortId] = useState<number | null>(null);
  
  // Tenant dropdown states
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [tenantOptions, setTenantOptions] = useState<string[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  // 3) Hardware info (read‐only) + Ports list
  const [hardwareData, setHardwareData] = useState<HardwareItem | null>(null);
  const [ports, setPorts] = useState<Port[]>([]);

  // 4) Spinner / error states
  const [isFetching, setIsFetching] = useState(false);

  // 5) Add/Edit Port modal
  const [isPortModalOpen, setIsPortModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Port form state management using custom hook
  const portForm = usePortForm();

  // 6) Delete confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [portToDelete, setPortToDelete] = useState<Port | null>(null);

  // 7) View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);

  // 8) Edit modal loading state
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  // --------------------------- useEffects ----------------------------------

  // A) Fetch datacenters and KE ports on mount (once user is authenticated)
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        
        // Fetch datacenters
        const resp = await getDataCentersIdsNames(token);
        const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        const shapedData: DataCenterItem[] = rawData.map(
          (dc: any) => ({
            datacenterId: dc.datacenterId,
            name: dc.name,
            displayName: dc.displayName ?? dc.name,
          })
        );
        setDataCenters(shapedData);
        // Convert to string array of names
        // Use displayName for dropdown options
        const dcNames = shapedData.map((dc) => dc.displayName ?? dc.name);
        setDataCenterOptions(["All", ...dcNames]);
        
        // Fetch KE ports list
        const kePortsResp = await getKEPorts(token);
        // Response structure: { success: true, message: "...", data: [...] }
        if (kePortsResp.data?.data && Array.isArray(kePortsResp.data.data)) {
          setPorts(shapePortData(kePortsResp.data.data));
        } else if (Array.isArray(kePortsResp.data)) {
          setPorts(shapePortData(kePortsResp.data));
        } else {
          console.warn("Unexpected KE ports response structure:", kePortsResp.data);
        }
        
        // Fetch tenants
        const tenantsResp = await getTenants(token);
        const tenantsDataRaw = tenantsResp.data?.data ?? tenantsResp.data ?? [];
        const tenantsData: TenantItem[] = Array.isArray(tenantsDataRaw) ? tenantsDataRaw : [];
        setTenants(tenantsData);
        // Create dropdown options: "nameDisplay - chargebeeCustomerId"
        const tenantLabels = tenantsData.map((tenant: TenantItem) => {
          const display = tenant.nameDisplay || tenant.Name || "";
          const chargebee = tenant.chargebeeCustomerId || "";
          return chargebee ? `${display} - ${chargebee}` : display;
        });
        setTenantOptions(tenantLabels);
      } catch (error) {
        console.error("Error fetching datacenters or KE ports:", error);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  //HELPER FUNCTIONS
  // A) Convert the stored ID (selectedHardwareId) into a hardware name
  function getHardwareNameById(hardwareId: number | null): string {
    if (!hardwareId) return "";
    const match = hardwareList.find((hw) => hw.id === hardwareId);
    return match ? match.hostname : "";
  }
  // --------------------------- Handlers ------------------------------------

  // 1) When user picks a DC name
  async function handleDataCenterSelect(dcName: string, isModal = false) {
    if (!isModal) {
      // ========== GLOBAL FILTER LOGIC ==========
      setSelectedDataCenterName(dcName);
      setSelectedHardwareId(null);
      setHardwareData(null);
      setPorts([]);
    } else {
      // ========== MODAL LOGIC ==========
      setModalDcName(dcName);
      setModalHwId(null);
    }

    // Suppose we have to convert name->id. For the example, pretend the name == id
    // 1) Find the matching object by name

    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();

      if (dcName === "All" && !isModal) {
        // fetch all
        console.log("inside if");
        const allPortsResp = await getAllPorts(token);
        setPorts(allPortsResp.data.ports);
        setSelectedDataCenterName("All");
        setSelectedDataCenterId(null);
        setHardwareOptions([]);
        return;
      }
      const matchedDc = dataCenters.find(
        (dc) =>
          dc.displayName === dcName ||
          dc.name === dcName ||
          dc.datacenterId === dcName
      );
      if (!matchedDc) {
        console.error("No data center found for name:", dcName);
        return;
      }
      const hwResp = await getHardwareListByDataCenter(
        token,
        matchedDc.datacenterId
      );
      // Save the fields needed for our dropdowns
      const hwRaw = (hwResp as any)?.data?.data ?? (hwResp as any)?.data ?? [];
      const shapedHardware: HardwareDropdownItem[] = hwRaw.map(
        (hw: any) => ({
          id: hw.id,
          hostname: hw.hostname,
        })
      );
      const hwNames = shapedHardware.map((hw) => hw.hostname);
      if (!isModal) {
        setHardwareList(shapedHardware);
        setHardwareOptions(hwNames);
        setSelectedDataCenterId(matchedDc.datacenterId);
      } else {
        setHardwareList(shapedHardware);
        setHardwareOptions(hwNames);
        setModalDcId(matchedDc.datacenterId);
        // Clear ports when datacenter changes
        setModalPortsList([]);
        setModalPortOptions([]);
        setSelectedPortId(null);
        setModalHwId(null);
      }
    } catch (err) {
      console.error("Error fetching hardware list:", err);
    } finally {
      setIsFetching(false);
    }
  }

  // 2) When user picks hardware
  async function handleHardwareSelect(hwName: string, isModal = false) {
    if (!isModal) {
      setHardwareData(null);
      setPorts([]);
    } else {
      setModalHwName(hwName);
      setModalHwId(null);
    }

    const matchedHw = hardwareList.find((hw) => hw.hostname === hwName);
    if (!matchedHw) {
      console.error("No hardware found for name:", hwName);
      return;
    }
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const resp = await getListPortsByHardwareId(token, matchedHw.id);
      
      // Handle both direct response and nested { data: {...} } response
      const responseData = resp.data?.data || resp.data;
      
      // Validate response structure before accessing properties
      if (!responseData) {
        console.error("Invalid response structure - no data:", resp.data);
        if (isModal) {
          setModalPortsList([]);
          setModalPortOptions([]);
          setSelectedPortId(null);
        }
        return;
      }

      const data: PortsByHardwareResponse = responseData;

      // Validate that hardware exists and has an id
      if (!data || !data.hardware || !data.hardware.id) {
        console.error("Invalid response structure - missing hardware or hardware.id:", data);
        if (isModal) {
          setModalPortsList([]);
          setModalPortOptions([]);
          setSelectedPortId(null);
        }
        return;
      }

      if (!isModal) {
        setHardwareData(data.hardware);
        setPorts(shapePortData(data.ports || []));
        setSelectedHardwareId(data.hardware.id);
      } else {
        setModalHwId(data.hardware.id);
        portForm.updateField("hardwareResourceId", data.hardware.id);
        // Populate ports dropdown for modal
        setModalPortsList(data.ports || []);
        const portLabels = (data.ports || []).map((port: any) => {
          const portNumber = port.portNumber || "";
          const portDescription = port.portDescription || "";
          const portStatus = port.portStatus || "";
          return `${portNumber} - ${portDescription} - ${portStatus}`;
        });
        setModalPortOptions(portLabels);
        // Reset selected port when hardware changes
        setSelectedPortId(null);
      }
    } catch (err) {
      console.error("Error fetching ports by hardware:", err);
      // Clear ports on error
      if (isModal) {
        setModalPortsList([]);
        setModalPortOptions([]);
        setSelectedPortId(null);
      }
    } finally {
      setIsFetching(false);
    }
  }

  // Handler for port selection in modal
  function handlePortSelect(portLabel: string) {
    const matchedPort = modalPortsList.find((port: any) => {
      const portNumber = port.portNumber || "";
      const portDescription = port.portDescription || "";
      const portStatus = port.portStatus || "";
      const label = `${portNumber} - ${portDescription} - ${portStatus}`;
      return label === portLabel;
    });
    
    if (matchedPort) {
      setSelectedPortId(matchedPort.id);
      // Populate form fields with selected port data (but NOT portName)
      portForm.updateFields({
        portNumber: matchedPort.portNumber || "",
        portStatus: matchedPort.portStatus || "",
      });
    }
  }

  function getPortLabelById(portId: number | null): string {
    if (!portId) return "";
    const port = modalPortsList.find((p: any) => p.id === portId);
    if (!port) return "";
    const portNumber = port.portNumber || "";
    const portDescription = port.portDescription || "";
    const portStatus = port.portStatus || "";
    return `${portNumber} - ${portDescription} - ${portStatus}`;
  }

  function getTenantLabelById(tenantId: number | null): string {
    if (!tenantId) return "";
    const tenant = tenants.find((t) => t.id === tenantId);
    if (!tenant) return "";
    const display = tenant.nameDisplay || tenant.Name || "";
    const chargebee = tenant.chargebeeCustomerId || "";
    return chargebee ? `${display} - ${chargebee}` : display;
  }

  function handleTenantSelect(tenantLabel: string) {
    const matchedTenant = tenants.find((tenant) => {
      const display = tenant.nameDisplay || tenant.Name || "";
      const chargebee = tenant.chargebeeCustomerId || "";
      const label = chargebee ? `${display} - ${chargebee}` : display;
      return label === tenantLabel;
    });
    
    if (matchedTenant) {
      setSelectedTenantId(matchedTenant.id);
      portForm.updateField("tenantId", matchedTenant.id);
    }
  }

  // 3) Add Port
  function handleAddPort() {
    setEditMode(false);
    // Reset form to initial state
    portForm.resetForm();
    // Clear modal selection state
    setModalDcId(null);
    setModalDcName("");
    setModalHwId(null);
    setModalHwName("");
    // Clear ports dropdown
    setModalPortsList([]);
    setModalPortOptions([]);
    setSelectedPortId(null);
    // Clear tenant selection
    setSelectedTenantId(null);
    setIsPortModalOpen(true);
  }

  async function handleSavePort() {
    // For physical ports, hardwareResourceId is required
    if (!portForm.formData.isVirtualPort && !portForm.formData.hardwareResourceId) {
      alert("Please select a hardware first.");
      return;
    }

    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();

      // Build payload from form data
      const payload: any = {
        // Basic port fields
        portName: portForm.formData.portName,
        portNumber: portForm.formData.portNumber,
        portType: portForm.formData.portType,
        portStatus: portForm.formData.portStatus,
        l2Description: portForm.formData.l2Description,
        l3Description: portForm.formData.l3Description,
        // Optional fields
        portId: portForm.formData.portId || undefined,
        subscriptionId: portForm.formData.subscriptionId || undefined,
        tenantId: portForm.formData.tenantId || undefined,
        isDraft: portForm.formData.isDraft,
        isVirtualPort: portForm.formData.isVirtualPort,
        // P2P Addressing
        mask: portForm.formData.mask || undefined,
        peerIp: portForm.formData.peerIp || undefined,
        gateway: portForm.formData.gateway || undefined,
        network: portForm.formData.network || undefined,
        // Tags and Terms
        tags: portForm.formData.tags && portForm.formData.tags.length > 0 ? portForm.formData.tags : undefined,
        terms: portForm.formData.terms || undefined,
      };

      // Add hardwareResourceId only for physical ports
      if (!portForm.formData.isVirtualPort && portForm.formData.hardwareResourceId) {
        payload.hardwareResourceId = portForm.formData.hardwareResourceId;
      }

      // Add provider fields only for virtual ports
      if (portForm.formData.isVirtualPort) {
        if (portForm.formData.editProvider) {
          payload.provider = portForm.formData.editProvider;
        }
        if (portForm.formData.editProviderAccount) {
          payload.providerAccount = portForm.formData.editProviderAccount;
        }
        if (portForm.formData.editProviderId) {
          payload.providerId = portForm.formData.editProviderId;
        }
        // Add crossconnect fields for virtual ports
        if (portForm.formData.crossconnectType || portForm.formData.crossconnectSpeed || portForm.formData.crossconnectId) {
          payload.crossconnectType = portForm.formData.crossconnectType || undefined;
          payload.crossconnectSpeed = portForm.formData.crossconnectSpeed || undefined;
          payload.crossconnectId = portForm.formData.crossconnectId || undefined;
        }
      }
      
      if (editMode && portForm.formData.id != null) {
        // Update an existing port
        payload.id = portForm.formData.id;
        await updatePort(token, portForm.formData.id, payload);
        setPorts((prevPorts) =>
          prevPorts.map((port) =>
            port.id === portForm.formData.id ? { ...port, ...payload } : port
          )
        );
      } else {
        // Add a new port
        await addPort(token, payload);
      }

      // Re-fetch ports
      if (hardwareData) {
        await handleHardwareSelect(String(hardwareData.id));
      }
    } catch (error) {
      console.error("Error saving port:", error);
    } finally {
      setIsFetching(false);
      setIsPortModalOpen(false);
    }
  }

  // 4) Edit Port
  async function handleEditPort(row: Port) {
    try {
      setEditMode(true);
      setIsPortModalOpen(true);
      setIsEditLoading(true);
      setSelectedPort(null);
      
      // Fetch fresh port details
      const token = await getAccessTokenSilently();
      const response = await getKEPortById(token, row.id);
      // Handle response structure: { success, message, data: {...} }
      const rawPortData = response.data?.data || response.data;
      // Shape the data to match Port interface
      const shapedPort = shapePortData([rawPortData])[0];
      setSelectedPort(shapedPort);
      
      // Use the fetched port data instead of row data
      const port = shapedPort;
      
      // Determine if port is virtual (has config object)
      const isVirtual = !!(rawPortData.config);
      
      // Parse crossconnect details if available - use raw data to ensure we get the exact structure
      let crossconnectType = "";
      let crossconnectSpeed = "";
      let crossconnectId = "";
      
      if (rawPortData.config?.crossConnect) {
        const crossConnect = rawPortData.config.crossConnect;
        crossconnectId = crossConnect.id || "";
        
        // Parse details: "SMF, 10G, ConsoleConnect Test" -> type: "SMF", speed: "10G"
        if (crossConnect.details) {
          const detailsParts = crossConnect.details.split(",").map((part: string) => part.trim());
          if (detailsParts.length >= 1) {
            crossconnectType = detailsParts[0]; // First part is type (e.g., "SMF")
          }
          if (detailsParts.length >= 2) {
            crossconnectSpeed = detailsParts[1]; // Second part is speed (e.g., "10G")
          }
        }
      }
      
      // Handle tags - can be array or string
      let tagsArray: string[] = [];
      if (Array.isArray((port as any).tag)) {
        tagsArray = (port as any).tag;
      } else if (typeof (port as any).tag === "string") {
        // If tag is a string, split by comma or use as single tag
        tagsArray = (port as any).tag.includes(",") 
          ? (port as any).tag.split(",").map((t: string) => t.trim()).filter((t: string) => t.length > 0)
          : [(port as any).tag].filter((t: string) => t.length > 0);
      }
      
      // Populate form with fetched port data
      portForm.setFormData({
        id: port.id,
        portId: port.portId || (port as any).subid || "",
        portName: port.portName || (port as any).name || "",
        portNumber: port.portNumber || "",
        portType: port.portType || "",
        portStatus: port.portStatus || "",
        hardwareResourceId: port.hardwareResourceId || null,
        editHardwareId: port.hardwareId || (port as any).hardwareId || "",
        editCapacityMbps:
          (port as any).capacityMbps !== undefined && (port as any).capacityMbps !== null
            ? String((port as any).capacityMbps)
            : "",
        editProvider: (port as any).provider || rawPortData.provider || "",
        editProviderAccount: rawPortData.providerAccount !== undefined && rawPortData.providerAccount !== null 
          ? String(rawPortData.providerAccount) 
          : "",
        editProviderId: (port as any).providerId || rawPortData.providerId || "",
        editLagId: (port as any).lagId || "",
        editCreatedAt: (port as any).createdAt || "",
        editTenantName: port.tenant?.nameDisplay || port.tenant?.Name || "",
        editChargebeeId: port.tenant?.chargebeeCustomerId || "",
        editLineId: (port as any).odooMetadata?.lineId !== undefined ? String((port as any).odooMetadata.lineId) : "",
        editOrderId: (port as any).odooMetadata?.orderId !== undefined ? String((port as any).odooMetadata.orderId) : "",
        editCorrelation: (port as any).odooMetadata?.correlation || "",
        editDcId: port.datacenter?.datacenterId || "",
        editDcName: port.datacenter?.name || "",
        editDcDisplayName: port.datacenter?.displayName || "",
        editDcCity: port.datacenter?.city || "",
        // Map isDraft: true (Draft) -> 0, isDraft: false (Active) -> 1
        isDraft: (port as any).isDraft ? 0 : 1,
        subscriptionId: (port as any).subscriptionId || (port as any).subid || "",
        tags: tagsArray,
        terms: "",
        crossconnectType: crossconnectType,
        crossconnectSpeed: crossconnectSpeed,
        crossconnectId: crossconnectId,
        tenantId: (port as any).tenantId || port.tenant?.id || null,
        isVirtualPort: isVirtual,
      });
      
      // Set tenant selection
      const tenantId = (port as any).tenantId || port.tenant?.id || null;
      setSelectedTenantId(tenantId);
      
      // For physical ports, set location selection and fetch hardware list
      if (!isVirtual && port.datacenter?.datacenterId) {
        setModalDcName(port.datacenter.displayName || port.datacenter.name || "");
        // Fetch hardware list for the datacenter
        try {
          const hwResponse = await getHardwareListByDataCenter(token, port.datacenter.datacenterId);
          const hwData = hwResponse.data?.data || hwResponse.data || [];
          const hwList = Array.isArray(hwData) ? hwData : [];
          const hwNames = hwList.map((hw: any) => hw.hostname || hw.serialNumber || "");
          setHardwareList(hwList.map((hw: any) => ({ id: hw.id, hostname: hw.hostname || hw.serialNumber || "" })));
          //setHardwareOptions(hwList.map((hw: any) => hw.hostname || hw.serialNumber || ""));
          
          // Try to find and set the hardware ID if available
          if ((port as any).hardwareId && (port as any).hardwareId !== "DRAFT-PENDING-PROVISION") {
            // Try to find the hardware in the list
            const foundHw = hwList.find((hw: any) => String(hw.id) === String((port as any).hardwareId));
            if (foundHw) {
              setModalHwId(foundHw.id);
              setHardwareOptions(hwNames);
            } else {
              setModalHwId(null);
              // If hardwareId is not found but exists, add it to options
              const hardwareIdStr = String((port as any).hardwareId);
              if (hardwareIdStr && !hwNames.includes(hardwareIdStr)) {
                setHardwareOptions([...hwNames, hardwareIdStr]);
              } else {
                setHardwareOptions(hwNames);
              }
            }
          } else {
            setModalHwId(null);
            // If hardwareId is a string like "DRAFT-PENDING-PROVISION", add it to options
            const hardwareIdStr = String((port as any).hardwareId || "");
            if (hardwareIdStr && !hwNames.includes(hardwareIdStr)) {
              setHardwareOptions([...hwNames, hardwareIdStr]);
            } else {
              setHardwareOptions(hwNames);
            }
          }
        } catch (error) {
          console.error("Error fetching hardware list:", error);
          setModalHwId(null);
        }
      } else {
        setModalDcName("");
        setModalHwId(null);
      }
    } catch (error) {
      console.error("Error fetching port details:", error);
    } finally {
      setIsEditLoading(false);
    }
  }

  // 5) Delete Port
  function handleDeletePort(row: Port) {
    setPortToDelete(row);
    setIsAlertOpen(true);
  }

  async function confirmDeletePort() {
    if (!portToDelete) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await deletePort(token, portToDelete.id);
      // Remove from local list or re-fetch
      setPorts((prev) => prev.filter((p) => p.id !== portToDelete.id));
    } catch (error) {
      console.error("Error deleting port:", error);
    } finally {
      setIsFetching(false);
      setPortToDelete(null);
      setIsAlertOpen(false);
    }
  }
  //6 View Port
  const handleViewPort = async (port: Port) => {
    try {
      setIsViewModalOpen(true);
      setIsViewLoading(true);
      setSelectedPort(null);
      const token = await getAccessTokenSilently();
      const response = await getKEPortById(token, port.id);
      // Handle response structure: { success, message, data: {...} }
      const rawPortData = response.data?.data || response.data;
      // Shape the data to match Port interface
      const shapedPort = shapePortData([rawPortData])[0];
      setSelectedPort(shapedPort);
    } catch (error) {
      console.error("Error viewing port:", error);
    } finally {
      setIsViewLoading(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const baseColumns = portColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: Port) => (
          <div className="flex items-center gap-2">
            {/* View */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleViewPort(row)}
            >
              <IconEye className="w-6 h-6" />
            </button>

            {/* Edit */}
            <button
              className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
              onClick={() => handleEditPort(row)}
            >
              <IconEdit className="w-10 h-10" />
            </button>

            {/* Delete */}
            <button
              className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
              onClick={() => handleDeletePort(row)}
            >
              <IconDelete className="w-6 h-6" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  const suggestedKeys = useMemo(() => {
    const headers = portColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [portColumns, isViewingAllCustomers]);

  function parseAvailability(raw: string): boolean | "unknown" {
    const s = raw.trim().toLowerCase();
    if (["true","1","yes","available","up","active"].includes(s)) return true;
    if (["false","0","no","down","inactive"].includes(s)) return false;
    return "unknown";
  }

  function makePortFields(): FilterField<Port>[] {
    return [
      { label: "Port Number", key: "portnumber", type: "string", accessor: r => r.portNumber },
      { label: "Port ID",     key: "portid",     type: "string", accessor: r => r.id },
      { label: "Port Type",   key: "porttype",   type: "string", accessor: r => r.portType },
      { label: "Port Status", key: "portstatus", type: "string", accessor: r => r.portStatus },
      {
        label: "Available",
        key: "available",
        type: "boolean",
        accessor: r => r.isAvailable,
        parseRhs: parseAvailability,
      },
    ];
  }

  // Build fields map
  const portFields = useMemo(() => makePortFields(), []);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Port>>();
    for (const f of portFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [portFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of portFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [portFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Port>(normalizedFilters, portFields),
    [normalizedFilters, portFields]
  );

  // filtered list
  const filteredPorts = useMemo(
    () => ports.filter(predicate),
    [ports, predicate]
  );

  // ------------------------------------------------------------------------

  return (
    <div className="flex-col space-y-5">

      {/* Hardware Details (read‐only)  HIDE FOR THE MOMENT*/}
      {/* {hardwareData && (
				<div className="p-4 mb-4 border border-semantic-border-secondary rounded-sm">
					<h2 className="text-lg font-semibold mb-4">Hardware Details</h2>
					<div className="grid grid-cols-3 gap-4">
						<GenericInput
							label="Data Center ID"
							value={hardwareData.datacenterId}
							readOnly
						/>
						<GenericInput
							label="Hardware ID"
							value={getHardwareNameById(selectedHardwareId)}
							readOnly
						/>
						<GenericInput
							label="Serial Number"
							value={hardwareData.serialNumber}
							readOnly
						/>
						<GenericInput label="Model" value={hardwareData.model} readOnly />
						<GenericInput label="Vendor" value={hardwareData.vendor} readOnly />
						<GenericInput
							label="Hostname"
							value={hardwareData.hostname}
							readOnly
						/>
					</div>
				</div>
			)} */}
      <GenericCard isCollapsible={false}>
        {/* Ports Table (List) */}
        <div className="p-5">
          <div className="flex justify-between my-5">
            <div className="w-[520px]"> 
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
            </div>
            <GenericButton
            label={
              <div className="flex items-center gap-2">
                <IconPlus className="w-4 h-4" />
                <span>Add Port</span>
              </div>
            }
            variant="brand"
            size="md"
            onClick={handleAddPort}
          />
          </div>
          {/* The table's own slot in skeleton. The overlay this replaces covered
              the rail and the views bar for the whole wait (CTX-1028). */}
          {isFetching ? (
            <TableSkeleton columns={baseColumns.length} rows={5} />
          ) : (
            <GenericTable
              columns={baseColumns}
              data={filteredPorts}
              initialRowsPerPage={filteredPorts.length > 0 ? Math.max(10, filteredPorts.length) : 10}
              rowsPerPageOptions={[10, 15, 20]}
              tableClassName="bg-[var(--semantic-bg-primary)]"
              width="100%"
              maxTableBodyHeight={
                filteredPorts.length > 0 
                  ? `${Math.min(Math.max(500, filteredPorts.length * 45 + 60), 1000)}px`
                  : "500px"
              }
            />
          )}
        </div>

        {/* Create Modal */}
        {!editMode && (
          <PortCreateModal
            isOpen={isPortModalOpen}
            onClose={() => setIsPortModalOpen(false)}
            onSave={handleSavePort}
            dataCenterOptions={dataCenterOptions}
            hardwareOptions={hardwareOptions}
            modalDcName={modalDcName}
            modalHwId={modalHwId}
            getHardwareNameById={getHardwareNameById}
            handleDataCenterSelect={handleDataCenterSelect}
            handleHardwareSelect={handleHardwareSelect}
            portOptions={modalPortOptions}
            selectedPortId={selectedPortId}
            getPortLabelById={getPortLabelById}
            handlePortSelect={handlePortSelect}
            portForm={portForm}
            tenantOptions={tenantOptions}
            selectedTenantId={selectedTenantId}
            getTenantLabelById={getTenantLabelById}
            handleTenantSelect={handleTenantSelect}
          />
        )}

        {/* Edit Modal */}
        {editMode && (
          <PortEditModal
            isOpen={isPortModalOpen}
            isLoading={isEditLoading}
            onClose={() => {
              setIsPortModalOpen(false);
              setEditMode(false);
              setSelectedPort(null);
              setIsEditLoading(false);
            }}
            onSave={handleSavePort}
            dataCenterOptions={dataCenterOptions}
            hardwareOptions={hardwareOptions}
            modalDcName={modalDcName}
            modalHwId={modalHwId}
            getHardwareNameById={getHardwareNameById}
            handleDataCenterSelect={handleDataCenterSelect}
            handleHardwareSelect={handleHardwareSelect}
            portOptions={modalPortOptions}
            selectedPortId={selectedPortId}
            getPortLabelById={getPortLabelById}
            handlePortSelect={handlePortSelect}
            portForm={portForm}
            tenantOptions={tenantOptions}
            selectedTenantId={selectedTenantId}
            getTenantLabelById={getTenantLabelById}
            handleTenantSelect={handleTenantSelect}
          />
        )}

        <PortViewModal
          isOpen={isViewModalOpen}
          isLoading={isViewLoading}
          port={selectedPort}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedPort(null);
            setIsViewLoading(false);
          }}
        />
        {/* Delete Confirmation Alert */}
        <GenericAlert
          isOpen={isAlertOpen}
          onClose={() => setIsAlertOpen(false)}
          onConfirm={confirmDeletePort}
          title="Are you sure?"
          description="This action cannot be undone. This will permanently delete the port."
          cancelLabel="Cancel"
          confirmLabel="Delete"
          iconWidthClass="w-20 h-12"
        />
      </GenericCard>
    </div>
  );
}
